import type { AIAppliedChange, AIReportReview, AIReviewFinding, AIReviewSubjectType } from "../../../domain/ai-review/ai-review-model.js";
import type { AISettings } from "../../../domain/ai-review/ai-settings.js";
import type { KnowledgeEntry } from "../../../domain/ai-review/technical-knowledge.js";
import type { AIReviewRepository } from "../../../repositories/ai-review-repository.js";

const KEYS = {
  reviews: "inspect_ai_reviews",
  findings: "inspect_ai_review_findings",
  changes: "inspect_ai_review_changes",
  knowledge: "inspect_ai_knowledge",
  settings: "inspect_ai_settings"
} as const;

function read<T>(key: string, fallback: T): T {
  try {
    if (typeof localStorage === "undefined") return fallback;
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(key, JSON.stringify(value));
}

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

/** Modo navegador: memória espelhada no localStorage. Servidor: `SqliteAIReviewRepository`. */
export class InMemoryAIReviewRepository implements AIReviewRepository {
  private reviews = read<AIReportReview[]>(KEYS.reviews, []);
  private findings = read<AIReviewFinding[]>(KEYS.findings, []);
  private changes = read<AIAppliedChange[]>(KEYS.changes, []);
  private knowledge = read<KnowledgeEntry[]>(KEYS.knowledge, []);
  private settings = read<AISettings | null>(KEYS.settings, null);

  private reload(): void {
    this.reviews = read(KEYS.reviews, this.reviews);
    this.findings = read(KEYS.findings, this.findings);
    this.changes = read(KEYS.changes, this.changes);
    this.knowledge = read(KEYS.knowledge, this.knowledge);
    this.settings = read(KEYS.settings, this.settings);
  }

  async saveReview(review: AIReportReview): Promise<void> {
    this.reload();
    this.reviews = [...this.reviews.filter(r => r.id !== review.id), clone(review)];
    write(KEYS.reviews, this.reviews);
  }

  async findReview(id: string): Promise<AIReportReview | null> {
    this.reload();
    const r = this.reviews.find(x => x.id === id);
    return r ? clone(r) : null;
  }

  async latestReview(subjectType: AIReviewSubjectType, reportId: string): Promise<AIReportReview | null> {
    this.reload();
    const list = this.reviews.filter(r => r.subjectType === subjectType && r.reportId === reportId).sort((a, b) => b.startedAt.localeCompare(a.startedAt));
    return list[0] ? clone(list[0]) : null;
  }

  async listReviews(filter: { clientId?: string; subjectType?: AIReviewSubjectType; reportId?: string; limit?: number }): Promise<AIReportReview[]> {
    this.reload();
    return this.reviews
      .filter(r => (!filter.clientId || r.clientId === filter.clientId) && (!filter.subjectType || r.subjectType === filter.subjectType) && (!filter.reportId || r.reportId === filter.reportId))
      .sort((a, b) => b.startedAt.localeCompare(a.startedAt))
      .slice(0, filter.limit ?? 500)
      .map(clone);
  }

  async saveFindings(findings: readonly AIReviewFinding[]): Promise<void> {
    this.reload();
    const ids = new Set(findings.map(f => f.id));
    this.findings = [...this.findings.filter(f => !ids.has(f.id)), ...findings.map(clone)];
    write(KEYS.findings, this.findings);
  }

  async listFindings(reviewId: string): Promise<AIReviewFinding[]> {
    this.reload();
    return this.findings.filter(f => f.reviewId === reviewId).map(clone);
  }

  async findFinding(id: string): Promise<AIReviewFinding | null> {
    this.reload();
    const f = this.findings.find(x => x.id === id);
    return f ? clone(f) : null;
  }

  async appendChange(change: AIAppliedChange): Promise<void> {
    this.reload();
    if (this.changes.some(c => c.id === change.id)) throw new Error("applied change already recorded");
    this.changes = [...this.changes, clone(change)];
    write(KEYS.changes, this.changes);
  }

  async listChanges(filter: { subjectType?: AIReviewSubjectType; reportId?: string }): Promise<AIAppliedChange[]> {
    this.reload();
    return this.changes.filter(c => (!filter.subjectType || c.subjectType === filter.subjectType) && (!filter.reportId || c.reportId === filter.reportId)).map(clone);
  }

  async saveKnowledge(entry: KnowledgeEntry): Promise<void> {
    this.reload();
    this.knowledge = [...this.knowledge.filter(k => k.id !== entry.id), clone(entry)];
    write(KEYS.knowledge, this.knowledge);
  }

  async listKnowledge(clientId: string | null): Promise<KnowledgeEntry[]> {
    this.reload();
    return this.knowledge.filter(k => k.clientId === null || k.clientId === clientId).map(clone);
  }

  async loadSettings(): Promise<AISettings | null> {
    this.reload();
    return this.settings ? clone(this.settings) : null;
  }

  async saveSettingsIfVersion(settings: AISettings, expectedVersion: number): Promise<boolean> {
    this.reload();
    if ((this.settings?.version ?? 0) !== expectedVersion) return false;
    this.settings = clone(settings);
    write(KEYS.settings, this.settings);
    return true;
  }
}
